import type { BetterAuthInstance, PhoneVerificationPlugin } from "@dmca/auth";
import { DateTime } from "@dmca/utils/luxon";
import { inferAdditionalFields } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

const phoneVerificationClient = () => {
    return {
        id: "phone-verification-client" as const,
        $InferServerPlugin: {} as PhoneVerificationPlugin,
    };
};

// Explicit annotation required: better-auth only exposes some of the types
// behind the inferred client from a deep internal path, so letting TS infer
// the return type breaks declaration emit (TS2742).
type AuthClient = ReturnType<
    typeof createAuthClient<{
        plugins: [
            ReturnType<typeof inferAdditionalFields<BetterAuthInstance>>,
            ReturnType<typeof phoneVerificationClient>,
        ];
    }>
>;

export const makeAuthClient = (opts?: { baseURL?: string }): AuthClient => {
    const baseURL = opts?.baseURL || import.meta.env.VITE_API_URL || "";
    return createAuthClient({
        baseURL,
        plugins: [
            inferAdditionalFields<BetterAuthInstance>(),
            phoneVerificationClient(),
        ],
    });
};

export const authClient: AuthClient = makeAuthClient();

type SessionResult = Awaited<ReturnType<AuthClient["getSession"]>>;

/**
 * Single source of truth for whether a getSession result is usable.
 *
 * Every route guard that redirects based on the session MUST use this check.
 * The /_authed and /login beforeLoads read the same cached session; if they
 * apply different predicates (e.g. one checks expiry and the other only
 * checks existence), an expired-but-present session makes them redirect to
 * each other forever ("Maximum update depth exceeded").
 */
export function isSessionValid(session: SessionResult): boolean {
    return (
        !session.error &&
        !!session.data?.user &&
        session.data.session.expiresAt.valueOf() > DateTime.now().valueOf()
    );
}
